import { Button } from '@/components/Button';
import { useLocale } from '@/providers/LocaleProvider';
import InfoCircleIcon from '@/resources/images/icons-info-circle.svg';
import { KLAYO_COLORS } from '@/themes';
import { formatCurrency } from '@/utilities/formatCurrency';
import styled from '@emotion/styled';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Alert, Box, Divider, IconButton, Paper, Radio, Stack, Typography } from '@mui/material';

export type PaymentOption = 'useBalance' | 'addToCart';

interface Props {
  coursesCount: number;
  studentsCount: number;
  enrolmentsCount: number;
  hasSelections: boolean;
  /**
   * The selection holds own-portal courses: nothing is paid, so no payment options, cost or price
   * wording. Takes precedence over `isFreeOrder`, which own-portal courses also satisfy.
   */
  isOwnPortal: boolean;
  ownPortalName: string;
  /** Own portal only: why the button is disabled when courses × students is over the cap. */
  ownPortalLimitMessage: string | null;
  isFreeOrder: boolean;
  paymentOption: PaymentOption;
  onPaymentOptionChange: (option: PaymentOption) => void;
  canUseBalance: boolean;
  canAddToCart: boolean;
  amount?: number;
  isUnlimitedCredit?: boolean;
  onBalanceInfoClick: () => void;
  /** Caption under "Purchase separately" when card checkout is unavailable. */
  cartUnavailableReason: string;
  totalCost: number;
  totalDiscountAmount: number;
  checkoutError: string | null;
  onCheckoutErrorClose: () => void;
  buttonLabel: string;
  buttonDisabled: boolean;
  onButtonClick: () => void;
}

/** The "Your selection" panel of the Enrol students dialog: counts, payment options and the action. */
export const EnrolSummaryPanel = ({
  coursesCount,
  studentsCount,
  enrolmentsCount,
  hasSelections,
  isOwnPortal,
  ownPortalName,
  ownPortalLimitMessage,
  isFreeOrder,
  paymentOption,
  onPaymentOptionChange,
  canUseBalance,
  canAddToCart,
  amount,
  isUnlimitedCredit,
  onBalanceInfoClick,
  cartUnavailableReason,
  totalCost,
  totalDiscountAmount,
  checkoutError,
  onCheckoutErrorClose,
  buttonLabel,
  buttonDisabled,
  onButtonClick,
}: Props) => {
  const { translate } = useLocale();

  return (
    <Box
      sx={{
        width: {
          xs: '100%',
          md: 400,
        },
        flexShrink: 0,
      }}
    >
      <Paper
        variant='outlined'
        sx={{
          borderRadius: '16px',
          borderColor: KLAYO_COLORS.NeonBlueNB100,
          position: 'sticky',
          top: 24,
        }}
      >
        <Stack
          p={'24px 32px'}
          gap={0.5}
          bgcolor={KLAYO_COLORS.NeonBlueNB025}
          sx={{
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
          }}
        >
          <Typography variant='body2' color={KLAYO_COLORS.GreyGR900}>
            {translate('manage.enrolStudentsPage.yourSelection')}
          </Typography>
          <Typography variant='body1' fontWeight={500}>
            {translate('manage.enrolStudentsPage.coursesCount', {
              count: coursesCount,
            })}
            {' \u2022 '}
            {translate('manage.enrolStudentsPage.studentsCount', {
              count: studentsCount,
            })}
          </Typography>
        </Stack>

        {/* Payment Options — omitted for a free order: there is nothing to choose
            between, and both cards would name a payment that is not happening. */}
        {!isOwnPortal && isFreeOrder && (
          <Stack p={'16px 32px'}>
            <Typography variant='body1' fontWeight={500} color={KLAYO_COLORS.GreyGR700}>
              {translate('manage.enrolStudentsPage.noPaymentRequired')}
            </Typography>
            <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
              {translate('manage.enrolStudentsPage.freeCoursesNoCharge')}
            </Typography>
          </Stack>
        )}

        {hasSelections && !isOwnPortal && !isFreeOrder && (
          <Stack gap={2} p={'16px 32px'}>
            <Box
              onClick={() => {
                if (canUseBalance) onPaymentOptionChange('useBalance');
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: '16px',
                borderRadius: 3,
                border: 2,
                borderColor:
                  paymentOption === 'useBalance' && canUseBalance
                    ? KLAYO_COLORS.NeonBlueNB500
                    : canUseBalance
                      ? KLAYO_COLORS.NeonBlueNB050
                      : KLAYO_COLORS.GreyGR300,
                backgroundColor: canUseBalance ? KLAYO_COLORS.WhiteWH900 : KLAYO_COLORS.GreyGR100,
                cursor: canUseBalance ? 'pointer' : 'not-allowed',
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: canUseBalance
                    ? KLAYO_COLORS.NeonBlueNB300
                    : KLAYO_COLORS.NeonBlueNB050,
                },
              }}
            >
              <Radio
                checked={paymentOption === 'useBalance' && canUseBalance}
                disabled={!canUseBalance}
                onChange={() => onPaymentOptionChange('useBalance')}
                icon={<RadioButtonUncheckedIcon />}
                checkedIcon={<CheckCircleIcon />}
                sx={{
                  p: 0,
                  mr: 1.5,
                  color: KLAYO_COLORS.GreyGR300,
                  '&.Mui-checked': { color: KLAYO_COLORS.NeonBlueNB500 },
                }}
              />
              <Box>
                <Typography
                  variant='body1'
                  fontWeight={500}
                  color={canUseBalance ? KLAYO_COLORS.GreyGR700 : KLAYO_COLORS.GreyGR400}
                >
                  {translate('manage.enrolStudentsPage.useAvailableBalance')}
                </Typography>
                <Stack direction='row' alignItems='center' gap={0.5}>
                  <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                    {(amount || 0) > 0 || isUnlimitedCredit
                      ? translate('manage.enrolStudentsPage.balanceAfterEnrollment', {
                          amount: isUnlimitedCredit
                            ? translate('manage.unlimited')
                            : formatCurrency(amount || 0),
                        })
                      : translate('manage.enrolStudentsPage.notEnoughBalanceAvailable')}
                  </Typography>
                  <IconButton
                    sx={{
                      p: 0,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onBalanceInfoClick();
                    }}
                  >
                    <StyledInfoCircleIcon width={20} height={20} />
                  </IconButton>
                </Stack>
              </Box>
            </Box>

            <Box
              onClick={() => {
                if (canAddToCart) onPaymentOptionChange('addToCart');
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: '16px',
                borderRadius: '16px',
                border: 2,
                borderColor:
                  paymentOption === 'addToCart' && canAddToCart
                    ? KLAYO_COLORS.NeonBlueNB500
                    : canAddToCart
                      ? KLAYO_COLORS.NeonBlueNB050
                      : KLAYO_COLORS.GreyGR300,
                backgroundColor: canAddToCart ? KLAYO_COLORS.WhiteWH900 : KLAYO_COLORS.GreyGR100,
                cursor: canAddToCart ? 'pointer' : 'not-allowed',
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: canAddToCart
                    ? KLAYO_COLORS.NeonBlueNB300
                    : KLAYO_COLORS.NeonBlueNB050,
                },
              }}
            >
              <Radio
                checked={paymentOption === 'addToCart' && canAddToCart}
                disabled={!canAddToCart}
                onChange={() => onPaymentOptionChange('addToCart')}
                icon={<RadioButtonUncheckedIcon />}
                checkedIcon={<CheckCircleIcon />}
                sx={{
                  p: 0,
                  mr: 1.5,
                  color: KLAYO_COLORS.GreyGR300,
                  '&.Mui-checked': { color: KLAYO_COLORS.NeonBlueNB500 },
                }}
              />
              <Box>
                <Typography
                  variant='body1'
                  fontWeight={500}
                  color={canAddToCart ? KLAYO_COLORS.GreyGR700 : KLAYO_COLORS.GreyGR400}
                >
                  {translate('manage.enrolStudentsPage.purchaseSeparately')}
                </Typography>
                <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                  {canAddToCart
                    ? translate('manage.enrolStudentsPage.saveBalanceForLater')
                    : cartUnavailableReason}
                </Typography>
              </Box>
            </Box>
          </Stack>
        )}

        <Stack spacing={1.5} p={'16px 32px'}>
          <Stack direction={'row'} justifyContent={'space-between'} gap={1}>
            <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
              {translate('manage.enrolStudentsPage.courses')}
            </Typography>
            <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
              {coursesCount}
            </Typography>
          </Stack>
          <Stack direction={'row'} justifyContent={'space-between'} gap={1}>
            <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
              {translate('manage.enrolStudentsPage.students')}
            </Typography>
            <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
              {studentsCount}
            </Typography>
          </Stack>
          <Stack direction={'row'} justifyContent={'space-between'} gap={1}>
            <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
              {translate('manage.enrolStudentsPage.enrolments')}
            </Typography>
            <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
              {enrolmentsCount}
            </Typography>
          </Stack>
          <Divider />
        </Stack>

        {!isOwnPortal && totalDiscountAmount > 0 && paymentOption === 'useBalance' && (
          <Stack direction={'row'} justifyContent={'space-between'} gap={1} p={'16px 32px'} pt={0}>
            <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
              {translate('manage.enrolStudentsPage.discount')}
            </Typography>
            <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
              {formatCurrency(totalDiscountAmount)}
            </Typography>
          </Stack>
        )}
        {!isOwnPortal && (
          <Stack direction={'row'} justifyContent={'space-between'} gap={1} p={'16px 32px'} pt={0}>
            <Typography variant='h6' fontWeight={600} fontSize={'18px'}>
              {translate('manage.enrolStudentsPage.totalCost')}
            </Typography>
            <Typography variant='h6' fontWeight={600} fontSize={'18px'}>
              {formatCurrency(
                paymentOption === 'useBalance' ? totalCost - totalDiscountAmount : totalCost
              )}
            </Typography>
          </Stack>
        )}

        <Stack gap={2} p={'16px 32px'}>
          {checkoutError && (
            <Alert severity='error' onClose={onCheckoutErrorClose}>
              {checkoutError}
            </Alert>
          )}
          {ownPortalLimitMessage && <Alert severity='warning'>{ownPortalLimitMessage}</Alert>}
          <Button
            disabled={buttonDisabled}
            variant='filled'
            label={buttonLabel}
            sx={{
              width: '100%',
            }}
            onClick={onButtonClick}
          />

          <Typography
            variant='caption'
            color={KLAYO_COLORS.GreyGR500}
            textAlign='center'
            display='block'
          >
            {isOwnPortal
              ? translate('manage.enrolStudentsPage.ownPortalAccountCaption', {
                  integrationName: ownPortalName,
                })
              : translate('manage.enrolStudentsPage.pricesInUsd')}
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

const StyledInfoCircleIcon = styled(InfoCircleIcon)(() => ({
  width: 20,
  height: 20,

  '& path[fill="#1E88E5"]': {
    fill: KLAYO_COLORS.NeonBlueNB500,
  },
}));
